import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SplashScreen = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [showLogo, setShowLogo] = useState(false);
  const [showFullLogo, setShowFullLogo] = useState(false);

  useEffect(() => {
    // Start logo assembly after initial delay
    const logoTimer = setTimeout(() => {
      setShowLogo(true);
    }, 500);

    // Fade in full, single-piece logo on top to eliminate seams
    const fullTimer = setTimeout(() => {
      setShowFullLogo(true);
    }, 1400);

    // Complete splash screen after animation
    const completeTimer = setTimeout(() => {
      setIsVisible(false);
      onComplete();
    }, 3000);

    return () => {
      clearTimeout(logoTimer);
      clearTimeout(fullTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  // Build a 4x4 grid of slices from the actual logo.png
  const buildLogoPieces = () => {
    const SIZE = 160; // container size
    const GRID = 4;   // 4x4 grid (16 pieces)
    const PIECE = SIZE / GRID; // 40px
    const pieces = [];
    let idx = 0;
    for (let row = 0; row < GRID; row++) {
      for (let col = 0; col < GRID; col++) {
        const fromX = col < GRID / 2 ? -320 : 320;
        const fromY = row < GRID / 2 ? -320 : 320;
        const finalX = -SIZE / 2 + PIECE / 2 + col * PIECE; // center-based coords
        const finalY = -SIZE / 2 + PIECE / 2 + row * PIECE;
        pieces.push({
          id: `slice-${col}-${row}`,
          initial: { x: fromX, y: fromY, rotate: (col < GRID/2 ? -1 : 1) * 45, scale: 0.85, opacity: 0 },
          final: { x: finalX, y: finalY, rotate: 0, scale: 1, opacity: 1 },
          delay: 0.05 * idx + 0.1,
          w: PIECE,
          h: PIECE,
          bgX: -col * PIECE,
          bgY: -row * PIECE,
        });
        idx++;
      }
    }
    return pieces;
  };

  const logoPieces = buildLogoPieces();

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-[var(--color-background)]"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="relative flex items-center justify-center">
            {/* Logo Assembly Area (using actual logo.png sliced via background-position) */}
            <div className="relative w-40 h-40 flex items-center justify-center">
              {showLogo && (
                <>
                  {logoPieces.map((piece) => (
                    <motion.div
                      key={piece.id}
                      className="absolute"
                      style={{
                        width: `${piece.w}px`,
                        height: `${piece.h}px`,
                        backgroundImage: "url(/logo.png)",
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '160px 160px',
                        backgroundPosition: `${piece.bgX}px ${piece.bgY}px`,
                        imageRendering: 'auto',
                      }}
                      initial={piece.initial}
                      animate={{ ...piece.final, opacity: showFullLogo ? 0.2 : piece.final.opacity }}
                      transition={{
                        duration: 1.2,
                        delay: piece.delay,
                        type: 'spring',
                        stiffness: 80,
                        damping: 12,
                      }}
                    />
                  ))}

                  {/* Full logo overlay to eliminate visible seams after assembly */}
                  {showFullLogo && (
                    <motion.img
                      src="/logo.png"
                      alt="SeerahVerse Logo"
                      className="absolute w-40 h-40 object-contain"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                    />
                  )}
                </>
              )}
            </div>

            {/* Logo Pulse Effect */}
            {showLogo && (
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: 1.5,
                }}
              >
                <div className="w-40 h-40 border-2 border-[var(--color-primary)]/30 rounded-full" />
              </motion.div>
            )}

            {/* No text per request */}

            {/* Loading Dots */}
            <motion.div
              className="absolute bottom-16 flex justify-center space-x-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8, duration: 0.3 }}
            >
              {[0, 1, 2].map((index) => (
                <motion.div
                  key={index}
                  className="w-2 h-2 bg-[var(--color-primary)] rounded-full"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: index * 0.2,
                  }}
                />
              ))}
            </motion.div>

            {/* Background Particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-[var(--color-primary)]/30 rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 2 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                  }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
